import { useState, useEffect } from "react"
import { useAuth } from "./hooks/useAuth"
import { supabase } from "./lib/supabase"
import { Login } from "./components/Login"
import { Welcome } from "./components/Welcome"
import { Paywall } from "./components/Paywall"
import { DecisionZeroFlow } from "./components/DecisionZeroFlow"
import { Onboarding } from "./components/Onboarding"
import { ResetPassword } from "./components/ResetPassword"
import { OutcomeScreen } from "./components/OutcomeScreen"

const isResetPassword = window.location.pathname === "/reset-password"

export default function App() {
  const { session, loading, hasAccess } = useAuth()
  const [language, setLanguage] = useState<"tr" | "en">("en")
  const [showWelcome, setShowWelcome] = useState(true)
  const [lastDecision, setLastDecision] = useState<string | undefined>()
  const [lastOutcome, setLastOutcome] = useState<string | undefined>()
  const [lastSessionId, setLastSessionId] = useState<string | undefined>()
  const [onboardingDone, setOnboardingDone] = useState(
    () => localStorage.getItem("dz_onboarding_done") === "true"
  )

  useEffect(() => {
    if (session) fetchLastSession()
  }, [session])

  const fetchLastSession = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  const { data } = await supabase
    .from("sessions")
    .select("id, focus_decision, outcome, session_date")
    .eq("user_id", user.id)
    .order("session_date", { ascending: false })
    .limit(1)
    .single()

  if (data) {
    const today = new Date().toISOString().split("T")[0]
    if (data.session_date !== today) {
      setLastDecision(data.focus_decision)
      setLastOutcome(data.outcome)
      if (data.outcome === null) {
        setLastSessionId(data.id)
      }
    }
  }
}

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setShowWelcome(true)
    setLastDecision(undefined)
    setLastOutcome(undefined)
    setLastSessionId(undefined)
  }

  if (isResetPassword) {
    return <ResetPassword />
  }

  if (loading) {
    return (
      <div className="app" style={{ justifyContent: "center", alignItems: "center" }}>
        <div style={{ color: "var(--text-muted)", fontSize: "13px", letterSpacing: "0.1em" }}>...</div>
      </div>
    )
  }

  if (!onboardingDone) {
    return <Onboarding language={language} onComplete={() => setOnboardingDone(true)} onLanguageChange={setLanguage} />
  }

  if (!session) {
    return <Login onLanguageChange={setLanguage} />
  }

  if (!hasAccess) {
    return <Paywall language={language} onLogout={handleLogout} onLanguageChange={setLanguage} />
  }

  if (lastDecision && lastSessionId) {
    return (
      <div className="app">
        <div className="header">
          <h1>DECISION ZERO</h1>
          <div className="lang">
            <button onClick={() => setLanguage("en")} disabled={language === "en"}>EN</button>
            <button onClick={() => setLanguage("tr")} disabled={language === "tr"}>TR</button>
          </div>
        </div>
        <OutcomeScreen
          language={language}
          decision={lastDecision}
          sessionId={lastSessionId}
          onDone={() => {
            setLastDecision(undefined)
            setLastSessionId(undefined)
          }}
        />
      </div>
    )
  }

  if (showWelcome) {
    return (
      <Welcome
        language={language}
        onStart={() => setShowWelcome(false)}
        lastDecision={lastDecision}
        lastOutcome={lastOutcome}
        onLanguageChange={setLanguage}
      />
    )
  }

  return <DecisionZeroFlow initialLanguage={language} onLogout={handleLogout} />
}